import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Payment } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { green, grey, red } from '@mui/material/colors';
import { useConfirm } from 'material-ui-confirm';
import moment from 'moment';

import { PricingPage } from '../components/PricingPage';
import { IconInfo } from '../components/minicomponents/icons';
import GoogleIcon from '../icons/Google.webp';
import {
  trackClickLogoutProfile,
  trackClickManageSubscription,
  trackClickProfileTab,
} from '../utils/analytics/analytics';
import { trackSentryError } from '../utils/analytics/sentry';
import { redeemAppsumoCode } from '../utils/api';
import { getPlanByNumberOfCodesRedeemed } from '../utils/appsumo';
import { signOutUser } from '../utils/auth';
import { colors } from '../utils/colors';
import { STRIPE_URL_MANAGE_PLAN } from '../utils/const';
import { openCrispChat } from '../utils/crispUtils';
import { useUserCodes, useUserPlan } from '../utils/db';
import { useFirebaseLoginState, useIsPaidUserStatus } from '../utils/hooks';
import { RootState } from '../utils/store';

export default function Profile() {
  const { isRegistered, isAuthStateLoading, user } = useFirebaseLoginState();
  const state = useSelector((state: RootState) => state.appState);

  const isPaidUser = useIsPaidUserStatus(state?.subcription?.status);

  const appSumoCodes = useUserCodes(user?.uid);
  const isAppSumoUser = appSumoCodes?.length > 0;
  const codesRedeemed = appSumoCodes?.filter((x) => x.isRedeemed);
  const codesRefunded = appSumoCodes?.filter((x) => x.isRefunded);
  const codesRedeemedAndNotRefunded = codesRedeemed?.filter((x) => !x.isRefunded);
  const codesNotRedeemedAndNotRefunded = appSumoCodes?.filter(
    (x) => !x.isRedeemed && !x.isRefunded
  );
  const appSumoPlan = getPlanByNumberOfCodesRedeemed(
    codesRedeemedAndNotRefunded?.length
  );
  const isMaxAppSumoPlanReached = appSumoPlan?.id === 3;

  const { t } = useTranslation();

  useEffect(() => {
    if (!isAuthStateLoading && !isRegistered) {
      navigate('/');
    }
  }, [isRegistered, isAuthStateLoading]);

  const confirmLogout = useConfirm();
  const navigate = useNavigate();

  const { activeTabSlug } = useParams<{
    activeTabSlug?: 'account' | 'subscription';
  }>();
  const [activeTab, setActiveTab] = useState<'account' | 'subscription'>('account');

  useEffect(() => {
    if (activeTabSlug === 'account' || activeTabSlug === 'subscription') {
      setActiveTab(activeTabSlug);
    } else {
      // Default to 'account' tab if slug is invalid or missing
      setActiveTab('account');
      navigate('/app/profile/account', { replace: true });
    }
  }, [activeTabSlug, navigate]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: 'account' | 'subscription'
  ) => {
    // Track profile tab click with tab name
    trackClickProfileTab(newValue);

    // setActiveTab(newValue); // This will be handled by the useEffect listening to activeTabSlug
    navigate(`/app/profile/${newValue}`);
  };

  const displayName = user?.providerData[0]?.displayName;
  const userName = displayName?.split(' ')?.[0] ?? '';
  const userSurname = displayName?.split(' ')?.[1] ?? '';
  const userEmail = user?.email ?? '';

  const handleLogoutClick = () => {
    trackClickLogoutProfile();
    confirmLogout({
      title: t('website.profile.confirmLogoutTitle'),
      description: t('website.profile.confirmLogoutMessage'),
    }).then(async () => {
      try {
        await signOutUser('profile_screen');
      } catch (e: any) {
        trackSentryError('Error signing out [profile]:', {
          error: e.message,
        });
        console.error('Error signing out [profile]:', e);
      }
      navigate('/');
    });
  };

  function SubscriptionView() {
    function getManageSubscriptionButton() {
      return (
        <Button
          variant="outlined"
          sx={{
            maxWidth: '280px',
            border: 1,
            color: 'primary.main',
            borderColor: 'primary.main',
            '&:hover': {
              border: 1,
            },
          }}
          onClick={() => {
            trackClickManageSubscription();
            window.open(STRIPE_URL_MANAGE_PLAN, '_blank');
          }}
          startIcon={<Payment />}
        >
          {isPaidUser
            ? t('website.profile.manageSubscription')
            : t('website.profile.renewSubscription')}
        </Button>
      );
    }

    const subscriptionName = state?.stripe?.subscriptionName ?? '';

    // Create date objects - directly parse the ISO strings with moment
    const subscriptionPeriod = {
      startDate: moment(state?.subcription?.dateStart),
      endDate: moment(state?.subcription?.dateEnd),
    };

    const billingCycle = {
      startDate: moment(state?.subcription?.dateCycleStart),
      endDate: moment(state?.subcription?.dateCycleEnd),
    };

    function getSubscriptionDetailsView() {
      // Check if dates are expired by comparing with their actual values, not current time
      const isSubscriptionExpired =
        subscriptionPeriod.endDate.isValid() &&
        subscriptionPeriod.endDate.isBefore(moment());
      const isBillingCycleExpired =
        billingCycle.endDate.isValid() && billingCycle.endDate.isBefore(moment());

      // Calculate days left by comparing with the actual end dates
      const billingCycleDaysLeft = billingCycle.endDate.isValid()
        ? Math.max(0, billingCycle.endDate.diff(moment(), 'days'))
        : 0;

      return (
        <Grid container spacing={1}>
          <Grid size={{ mobile: 12, tablet: 5 }} sx={{ textAlign: 'right' }}>
            {t('website.profile.subscriptionDetailsStartDate')}
          </Grid>
          <Grid size={{ mobile: 12, tablet: 7 }}>
            <strong>
              {subscriptionPeriod.startDate.isValid()
                ? subscriptionPeriod.startDate.format('LLL')
                : 'Invalid date'}
            </strong>
          </Grid>
          {isSubscriptionExpired && (
            <>
              <Grid size={{ mobile: 12, tablet: 5 }} sx={{ textAlign: 'right' }}>
                {t('website.profile.subscriptionDetailsEndDate')}{' '}
              </Grid>
              <Grid size={{ mobile: 12, tablet: 7 }}>
                <strong style={{ color: isSubscriptionExpired ? red[500] : 'inherit' }}>
                  {subscriptionPeriod.endDate.isValid()
                    ? subscriptionPeriod.endDate.format('LLL')
                    : 'Invalid date'}
                </strong>
              </Grid>
            </>
          )}
          <Grid size={{ mobile: 12, tablet: 5 }} sx={{ textAlign: 'right' }}>
            {t('website.profile.subscriptionDetailsBillingCycleStart')}
          </Grid>
          <Grid size={{ mobile: 12, tablet: 7 }}>
            <strong>
              {billingCycle.startDate.isValid()
                ? billingCycle.startDate.format('LLL')
                : 'Invalid date'}
            </strong>
          </Grid>
          <Grid size={{ mobile: 12, tablet: 5 }} sx={{ textAlign: 'right' }}>
            {t('website.profile.subscriptionDetailsBillingCycleEnd')}{' '}
          </Grid>
          <Grid size={{ mobile: 12, tablet: 7 }}>
            <strong style={{ color: isBillingCycleExpired ? red[500] : 'inherit' }}>
              {billingCycle.endDate.isValid()
                ? billingCycle.endDate.format('LLL')
                : 'Invalid date'}
            </strong>
          </Grid>
          <Grid size={{ mobile: 12, tablet: 5 }} sx={{ textAlign: 'right' }}>
            {t('website.profile.subscriptionDetailsBillingCycleDaysLeft')}
          </Grid>
          <Grid size={{ mobile: 12, tablet: 7 }}>
            <strong>
              {billingCycle.endDate.isValid() ? billingCycleDaysLeft : 'N/A'}
            </strong>
          </Grid>
        </Grid>
      );
    }

    function getActiveSubscriptionView() {
      const planCode = state?.subcription?.planCode;
      const { plan, loading: planLoading } = useUserPlan(planCode);

      return (
        <Grid container spacing={1}>
          <Grid size={4}>
            <Grid container spacing={1}>
              <Grid size={{ mobile: 12, tablet: 6 }} sx={{ textAlign: 'right' }}>
                {t('website.profile.subscriptionDetailsActiveSubscription')}
              </Grid>
              <Grid size={{ mobile: 12, tablet: 6 }}>
                <strong>
                  {planLoading ? '...' : plan?.name || t('website.profile.unknownPlan')}
                </strong>
              </Grid>
              <Grid size={{ mobile: 12, tablet: 6 }} sx={{ textAlign: 'right' }}>
                {t('website.profile.subscriptionDetailsCreditsLeftLabel')}
              </Grid>
              <Grid size={{ mobile: 12, tablet: 6 }}>
                <strong>{state?.subcription?.credits ?? 0}</strong>
              </Grid>
            </Grid>
          </Grid>

          <Grid size={5}>{getSubscriptionDetailsView()}</Grid>

          <Grid size={3}>
            <Stack spacing={1}>
              <Typography color={'success.main'}>
                <strong>{t('website.profile.subscriptionDetailsUpdatePlan')}</strong>
              </Typography>
              {getManageSubscriptionButton()}
            </Stack>
          </Grid>
        </Grid>
      );
    }

    function getActiveLimitedSubscriptionView() {
      const isLimitedByCredits = !creditsLeft || creditsLeft <= 0;
      return (
        <Grid container spacing={2}>
          <Grid size={4}>
            <Stack spacing={1}>
              <Typography>
                {isLimitedByCredits ? (
                  <>
                    {t('website.profile.subscriptionDetailsActiveSubscription')}{' '}
                    <b>{subscriptionName}</b>
                  </>
                ) : (
                  <>
                    {t('website.profile.subscriptionDetailsSubscription')}{' '}
                    <b>{subscriptionName}</b>
                  </>
                )}
              </Typography>
              <Typography>
                <div
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'website.profile.subscriptionDetailsFinishedSubscription',
                      { creditsLeft }
                    ),
                  }}
                />
              </Typography>
            </Stack>
          </Grid>
          <Grid size={5}>{getSubscriptionDetailsView()}</Grid>
          <Grid size={3}>
            <Stack spacing={1}>
              <Typography color={'success.main'}>
                <strong>
                  {t('website.profile.subscriptionDetailsUpdateToContinueUsing')}
                </strong>
              </Typography>
              {getManageSubscriptionButton()}
            </Stack>
          </Grid>
        </Grid>
      );
    }

    function getCanceledSubscriptionView() {
      const subscriptionCancelAtMoment = moment(state?.stripe?.cancelAt);
      const isCancelationInFuture =
        state?.stripe?.cancelAt && subscriptionCancelAtMoment.isAfter(moment());
      const cancelAtFormattedString =
        state?.stripe?.cancelAt && subscriptionCancelAtMoment.format('LLL');
      return (
        <Grid container justifyContent={'center'}>
          <Grid size={{ mobile: 12, tablet: 6 }}>
            <Typography variant={'body1'}>
              <div
                dangerouslySetInnerHTML={{
                  __html: isCancelationInFuture
                    ? t(
                        'website.reqLeftCounter.canceledActive.futureCancelation.title',
                        { cancelAtFormattedString }
                      )
                    : t('website.reqLeftCounter.canceledActive.pastCancelation.title', {
                        cancelAtFormattedString,
                      }),
                }}
              />
            </Typography>
            <Typography variant={'body2'}>
              <div
                dangerouslySetInnerHTML={{
                  __html: isCancelationInFuture
                    ? t(
                        'website.reqLeftCounter.canceledActive.futureCancelation.subtitle',
                        { creditsLeft }
                      )
                    : t(
                        'website.reqLeftCounter.canceledActive.pastCancelation.subtitle',
                        { creditsLeft }
                      ),
                }}
              />
            </Typography>
          </Grid>

          <Grid size={{ mobile: 12, tablet: 4 }}>
            <Stack spacing={1} sx={{ maxWidth: '300px' }}>
              <Typography variant={'body2'} color={'success.main'}>
                <strong>{t('website.profile.subscriptionDetailsUpdatePlan')}</strong>
              </Typography>
              {getManageSubscriptionButton()}
            </Stack>
          </Grid>
        </Grid>
      );
    }

    function getCanceledInactiveSubscriptionView() {
      const subscriptionCancelAtMoment = moment(state?.stripe?.cancelAt);
      const isCancelationInFuture =
        state?.stripe?.cancelAt && subscriptionCancelAtMoment.isAfter(moment());
      const cancelAtFormattedString =
        state?.stripe?.cancelAt && subscriptionCancelAtMoment.format('LLL');

      return (
        <Grid container spacing={2}>
          <Grid size={{ mobile: 12, tablet: 4 }}>
            <Typography variant={'body1'}>
              <div
                dangerouslySetInnerHTML={{
                  __html: isCancelationInFuture
                    ? t(
                        'website.reqLeftCounter.canceledActive.futureCancelation.title',
                        { cancelAtFormattedString }
                      )
                    : t('website.reqLeftCounter.canceledActive.pastCancelation.title', {
                        cancelAtFormattedString,
                      }),
                }}
              />
            </Typography>
          </Grid>
          <Grid size={{ mobile: 12, tablet: 5 }}>{getSubscriptionDetailsView()}</Grid>
          <Grid size={{ mobile: 12, tablet: 3 }}>
            <Stack spacing={1}>
              <Typography variant={'body2'} color={'success.main'}>
                <strong>{t('website.profile.subscriptionDetailsUpdatePlan')}</strong>
              </Typography>
              {getManageSubscriptionButton()}
            </Stack>
          </Grid>
        </Grid>
      );
    }

    function getFreemiumSubscriptionView() {
      return (
        <Container sx={{ maxWidth: '900px' }}>
          <Typography
            dangerouslySetInnerHTML={{
              __html: t('website.profile.subscriptionDetailsFreemium', {
                creditsLeft,
              }),
            }}
          />
        </Container>
      );
    }

    const creditsLeft = state?.subcription?.credits ?? 0;

    switch (state?.subcription?.status) {
      case 'paid_active': // subscription is active and not limited
        return getActiveSubscriptionView();
      case 'paid_active_limited': // subscription is active and limited
        return getActiveLimitedSubscriptionView();
      case 'canceled_active': // subscription is canceled but still active
        return getCanceledSubscriptionView();
      case 'canceled_past_due': // subscription is canceled and expired
        return getCanceledInactiveSubscriptionView();
      case 'freemium': // subscription is freemium, showing nothing since already shown
        return getFreemiumSubscriptionView();
      case undefined:
        console.warn('Subscription status is undefined, showing empty pricing table.');
        return <></>;
      default:
        console.error('Unknown subscription status, showing empty pricing table.');
        return <></>;
    }
  }

  function AppSumoSubscriptionView() {
    const isTabletUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('tablet'));

    const [code, setCode] = React.useState('');
    const [isButtonStateLoading, setIsButtonStateLoading] = React.useState(false);
    const [codeError, setCodeError] = React.useState<string | undefined>();

    const onSubmitCodeClick = () => {
      setIsButtonStateLoading(true);
      setCodeError(undefined);

      redeemAppsumoCode(user?.uid, code)
        .then(() => {
          setIsButtonStateLoading(false);
        })
        .catch((error) => {
          trackSentryError(`Error redeeming code`, {
            code: code,
            error: error.message,
          });
          setIsButtonStateLoading(false);
          setCodeError(JSON.parse(error.message).message);
        });
    };

    return (
      <Grid container direction={'column'} spacing={2} sx={{ mt: -0.3 }}>
        <Grid>
          <Typography variant={'body1'}>
            <b>{appSumoPlan?.name}</b> 🚀
          </Typography>
          <Typography variant={'body2'} sx={{ color: 'success.main' }}>
            {t('website.profile.appsumoCodesRedeemed')}:{' '}
            <b>{codesRedeemedAndNotRefunded?.length}</b>
          </Typography>
          {!!codesRefunded?.length && (
            <Typography variant={'body2'} sx={{ color: grey[600] }}>
              {t('website.profile.appsumoCodesRefunded')}: <b>{codesRefunded.length}</b>
            </Typography>
          )}
          {!!codesNotRedeemedAndNotRefunded?.length && (
            <Typography variant={'body2'} sx={{ color: red[300] }}>
              {t('website.profile.appsumoCodesErrors')}:{' '}
              <b>{codesNotRedeemedAndNotRefunded.length}</b>
            </Typography>
          )}
        </Grid>
        {appSumoCodes?.map((code, index) => {
          return (
            <Grid>
              <Typography variant={'body2'}>
                {index + 1}. {t('website.profile.appsumoCode')}: <b>{code.code}</b>
              </Typography>
              <Typography
                variant={'body2'}
                sx={{ ml: 2 }}
                color={code.isRedeemed ? 'success.main' : red[300]}
              >
                {code.isRedeemed ? (
                  <>
                    {t('website.profile.appsumoCodeRedeemedAt')}:{' '}
                    <b>{moment(code.redeemedAt).format('LLL')}</b>
                  </>
                ) : (
                  <>
                    {t('website.profile.appsumoCodeWasNotRedeemedError')} (
                    <Link
                      href="#"
                      onClick={openCrispChat}
                      sx={{
                        color: red[300],
                        textDecoration: 'underline',
                      }}
                    >
                      {t('website.profile.appsumoContactSupport')}
                    </Link>
                    )
                  </>
                )}
              </Typography>
              {code.isRefunded && (
                <Typography
                  variant={'body2'}
                  sx={{ ml: 2 }}
                  color={code.isRefunded ? grey[600] : undefined}
                >
                  {t('website.profile.appsumoCodeRefundedAt')}:{' '}
                  <b>{moment(code.refundedAt).format('LLL')}</b>
                </Typography>
              )}
            </Grid>
          );
        })}
        {!isMaxAppSumoPlanReached && (
          <>
            <Grid size={12}>
              <TextField
                label={t('website.profile.appsumoAppSumoCode')}
                variant="outlined"
                sx={{ minWidth: isTabletUp ? 300 : '100%' }}
                placeholder={t('website.profile.appsumoCodeExample')}
                value={code}
                error={!!codeError}
                helperText={codeError}
                onChange={(e) => setCode(e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <LoadingButton
                onClick={onSubmitCodeClick}
                variant="contained"
                color={'success'}
                loading={isButtonStateLoading}
                sx={{
                  ...(isButtonStateLoading && {
                    background: `${green[100]} !important`,
                  }),
                }}
              >
                {t('website.profile.appsumoSubmitOneMoreCode')}
              </LoadingButton>
            </Grid>
          </>
        )}
      </Grid>
    );
  }

  const fullName = userName + ' ' + userSurname;

  return (
    <Container maxWidth="desktop" sx={{ mt: 6, mb: 4 }}>
      {/* <ReleaseNotificationBanner sx={{ mt: 2 }} /> */}
      {!isAuthStateLoading && (
        <>
          <Grid
            container
            spacing={4}
            justifyContent={'center'}
            sx={{
              bgcolor: 'background.default',
              borderRadius: '8px',
            }}
          >
            {/* Tabs */}
            <Grid size={12}>
              <Box sx={{ borderBottom: 1, borderColor: colors.border.default }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  aria-label="profile tabs"
                  centered
                  sx={{ position: 'relative', top: '1px' }}
                >
                  <Tab
                    label={t('website.profile.tabs.account')}
                    value="account"
                    sx={{ height: 64 }}
                  />
                  <Tab
                    label={t('website.profile.tabs.subscription')}
                    value="subscription"
                    sx={{ height: 64 }}
                  />
                </Tabs>
              </Box>
            </Grid>

            {/* Tab Content */}
            {activeTab === 'account' && (
              <Grid size={12}>
                <Container sx={{ maxWidth: '500px', paddingX: 5, mt: 4, mb: 8 }}>
                  <Stack spacing={4}>
                    <Stack spacing={1.5}>
                      <TextField
                        fullWidth
                        label={t('website.profile.fullName')}
                        value={fullName}
                        variant="outlined"
                        disabled
                      />
                      <Typography
                        sx={{
                          color: colors.text.primary,
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2,
                        }}
                      >
                        <IconInfo
                          sx={{
                            mr: 1,
                            color: colors.text.secondary,
                          }}
                        />
                        {t('website.profile.caption')}
                      </Typography>
                    </Stack>
                    <Stack spacing={1}>
                      <TextField
                        fullWidth
                        label={t('website.profile.email')}
                        value={userEmail}
                        variant="outlined"
                        disabled
                      />
                      <Stack direction="row" spacing={1} justifyContent="space-between">
                        <Typography
                          sx={{
                            color: colors.text.primary,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <img
                            src={GoogleIcon}
                            alt="google icon"
                            style={{ width: 24, height: 24, marginRight: 8 }}
                          />
                          {t('website.profile.googleConnected')}
                        </Typography>
                        <Button
                          variant="text"
                          size={'large'}
                          onClick={() => handleLogoutClick()}
                        >
                          {t('website.profile.logout')}
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                </Container>
              </Grid>
            )}

            {activeTab === 'subscription' && (
              <Grid size={12}>
                <Stack spacing={8} sx={{ paddingX: 5, mt: 4, mb: 8 }}>
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      {isAppSumoUser ? (
                        <AppSumoSubscriptionView />
                      ) : (
                        <SubscriptionView />
                      )}
                    </Grid>
                  </Grid>
                  {!isAppSumoUser && <PricingPage />}
                </Stack>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Container>
  );
}
